import { ChevronDown, X } from "lucide-react";
import { useEffect, useId, useMemo, useRef, useState, type JSX, type KeyboardEvent } from "react";

export interface SearchableOption {
  value: string;
  label: string;
}

interface SearchableSelectProps {
  options: SearchableOption[];
  /** Selected option value; "" means nothing selected. */
  value: string;
  onChange: (value: string) => void;
  /** Shown when nothing is selected, and as the input placeholder while searching. */
  placeholder: string;
  /** Copy for the empty result state, e.g. "No matches". */
  emptyText: string;
  /** Accessible name for the clear button. */
  clearLabel: string;
  "aria-label"?: string;
  "data-testid"?: string;
  className?: string;
  disabled?: boolean;
}

const INPUT_CLASS =
  "h-9 w-full rounded-[var(--control-r)] border border-[var(--premium-border-strong)] bg-white pl-3 pr-14 text-sm text-[var(--text-primary)] outline-none transition placeholder:text-[var(--text-subtle)] focus:border-[var(--brand-500)] focus-ring disabled:opacity-50";

/**
 * A select you can type into: the list filters as you type, arrow keys move
 * the highlight, Enter picks, Escape closes, and the × clears. Options live in
 * memory (no fetching) — pass the loaded list. Native `<select>` remains the
 * right choice for short fixed lists; this is for lists an operator would
 * otherwise scroll through.
 */
export function SearchableSelect({
  options,
  value,
  onChange,
  placeholder,
  emptyText,
  clearLabel,
  className,
  disabled = false,
  ...aria
}: SearchableSelectProps): JSX.Element {
  const listId = useId();
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState("");
  const [highlight, setHighlight] = useState(0);

  const selected = options.find((o) => o.value === value);
  const filtered = useMemo(() => {
    const q = term.trim().toLowerCase();
    return q ? options.filter((o) => o.label.toLowerCase().includes(q)) : options;
  }, [options, term]);
  const activeIndex = filtered.length === 0 ? 0 : Math.min(highlight, filtered.length - 1);

  // Close on outside click.
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e: MouseEvent): void => {
      if (containerRef.current?.contains(e.target as Node)) return;
      setOpen(false);
      setTerm("");
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  function pick(option: SearchableOption): void {
    onChange(option.value);
    setOpen(false);
    setTerm("");
    // Drop focus so the list does not spring back open on the next focus/click
    // event the browser delivers to the input after a pointer pick.
    inputRef.current?.blur();
  }

  function clear(): void {
    onChange("");
    setTerm("");
    setOpen(false);
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>): void {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setHighlight((h) => Math.min(h + 1, Math.max(filtered.length - 1, 0)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      if (open && filtered[activeIndex]) {
        e.preventDefault();
        pick(filtered[activeIndex]);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
      setTerm("");
    }
  }

  // While closed the input shows the selection; while open it is the search box.
  const inputValue = open ? term : (selected?.label ?? "");

  return (
    <div ref={containerRef} className={`relative ${className ?? ""}`}>
      <input
        ref={inputRef}
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-label={aria["aria-label"]}
        data-testid={aria["data-testid"]}
        className={INPUT_CLASS}
        placeholder={selected && !open ? selected.label : placeholder}
        value={inputValue}
        disabled={disabled}
        onFocus={() => {
          setOpen(true);
          setHighlight(0);
        }}
        onClick={() => setOpen(true)}
        onChange={(e) => {
          setTerm(e.target.value);
          setOpen(true);
          setHighlight(0);
        }}
        onKeyDown={onKeyDown}
      />
      <span className="pointer-events-none absolute right-2 top-1/2 flex -translate-y-1/2 items-center gap-1">
        {selected && !disabled && (
          <button
            type="button"
            aria-label={clearLabel}
            data-testid={aria["data-testid"] ? `${aria["data-testid"]}-clear` : undefined}
            onMouseDown={(e) => e.preventDefault()}
            onClick={clear}
            className="pointer-events-auto rounded p-0.5 text-[var(--text-subtle)] hover:text-[var(--text-primary)]"
          >
            <X className="h-3.5 w-3.5" aria-hidden />
          </button>
        )}
        <ChevronDown className="h-4 w-4 text-[var(--text-subtle)]" aria-hidden />
      </span>
      {open && (
        <ul
          id={listId}
          role="listbox"
          className="absolute left-0 top-full z-30 mt-1 max-h-64 w-full overflow-auto rounded-[var(--control-r)] border border-[var(--premium-border)] bg-white py-1 text-sm shadow-[var(--sh-card)]"
        >
          {filtered.length === 0 ? (
            <li className="px-3 py-2 text-[var(--text-subtle)]">{emptyText}</li>
          ) : (
            filtered.map((o, i) => (
              // Keyboard interaction lives on the combobox input (WAI-ARIA
              // combobox pattern); options are pointer targets only.
              // eslint-disable-next-line jsx-a11y/click-events-have-key-events
              <li
                key={o.value}
                role="option"
                aria-selected={o.value === value}
                data-highlighted={i === activeIndex || undefined}
                onMouseDown={(e) => e.preventDefault()}
                onMouseEnter={() => setHighlight(i)}
                onClick={() => pick(o)}
                className={`cursor-pointer px-3 py-1.5 ${
                  i === activeIndex ? "bg-[var(--row-hover)]" : ""
                } ${o.value === value ? "font-semibold" : ""}`}
              >
                {o.label}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
